// Embroidery add-on: keeps the shopper's choices when the product form re-renders on
// variant change, and gives s-product-form.js the extra cart line to add.
const store = (window.__luunaEmbroidery ||= {});

class LuunaEmbroidery extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-emb-toggle]');
    this.fields = this.querySelector('[data-emb-fields]');
    this.text = this.querySelector('[data-emb-text]');
    this.colour = this.querySelector('[data-emb-colour]');
    this.colourOther = this.querySelector('[data-emb-colour-other]');
    this.font = this.querySelector('[data-emb-font]');
    this.error = this.querySelector('[data-emb-error]');

    const saved = store[this.dataset.productId];
    if (saved) {
      this.toggle.checked = saved.enabled;
      this.text.value = saved.text;
      this.colour.value = saved.colour;
      this.colourOther.value = saved.colourOther || '';
      if (saved.font) this.font.value = saved.font;
      this.querySelectorAll('[data-emb-placement]').forEach((r) => { r.checked = r.value === saved.placement; });
    }
    this.#sync();
    this.addEventListener('input', () => { this.#save(); this.#sync(); this.showError(''); });
    this.addEventListener('change', () => { this.#save(); this.#sync(); });
  }

  get enabled() { return Boolean(this.toggle?.checked); }

  get placement() {
    return this.querySelector('[data-emb-placement]:checked')?.value || '';
  }

  #sync() {
    this.fields.hidden = !this.enabled;
    this.colourOther.hidden = this.colour.value !== 'other';
  }

  get colourValue() {
    return this.colour.value === 'other' ? this.colourOther.value.trim() : this.colour.value;
  }

  #save() {
    store[this.dataset.productId] = {
      enabled: this.enabled,
      text: this.text.value,
      colour: this.colour.value,
      colourOther: this.colourOther.value,
      font: this.font.value,
      placement: this.placement,
    };
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  // Returns { ok, attributes, line } for s-product-form.js.
  build(quantity, variantTitle) {
    if (!this.enabled) return { ok: true };
    const text = this.text.value.trim();
    if (!text) {
      this.showError(this.dataset.errorText || '');
      this.text.focus();
      return { ok: false };
    }
    const colourValue = this.colourValue;
    if (!colourValue) {
      this.showError(this.dataset.errorColour || '');
      this.colourOther.focus();
      return { ok: false };
    }
    const attributes = [
      { key: 'Embroidery text', value: text },
      { key: 'Thread colour', value: colourValue },
      { key: 'Placement', value: this.placement },
      { key: 'Font', value: this.font.value },
    ];
    const forLabel = [this.dataset.for, variantTitle].filter(Boolean).join(' - ');
    return {
      ok: true,
      attributes,
      line: {
        merchandiseId: this.dataset.variantId,
        quantity,
        attributes: [...attributes, { key: 'For', value: forLabel }],
      },
    };
  }
}

if (!customElements.get('luuna-embroidery')) {
  customElements.define('luuna-embroidery', LuunaEmbroidery);
}

// Logo embroidery add-on: $15 per item (Custom Embroidery variant), plus a one-time
// digitizing fee line when the shopper picks "New logo". "New logo" and "Logo on file"
// are checkboxes that act as a pair: ticking one clears the other.
class LuunaLogoEmbroidery extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-logo-toggle]');
    this.fields = this.querySelector('[data-logo-fields]');
    this.types = Array.from(this.querySelectorAll('[data-logo-type]'));
    this.newNote = this.querySelector('[data-logo-new-note]');
    this.error = this.querySelector('[data-logo-error]');
    const key = `logo-${this.dataset.productId}`;
    const saved = store[key];
    if (saved) {
      this.toggle.checked = saved.enabled;
      this.types.forEach((t) => { t.checked = t.dataset.logoType === saved.type; });
    }
    this.#sync();
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.dataset.logoType && target.checked) {
        this.types.forEach((t) => { if (t !== target) t.checked = false; });
      }
      store[key] = { enabled: this.enabled, type: this.selected?.dataset.logoType || '' };
      this.showError('');
      this.#sync();
    });
  }

  get enabled() { return Boolean(this.toggle?.checked); }

  get selected() { return this.types.find((t) => t.checked) || null; }

  #sync() {
    this.fields.hidden = !this.enabled;
    if (this.newNote) this.newNote.hidden = this.selected?.dataset.logoType !== 'new';
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  // Returns { ok, attributes, lines } for s-product-form.js.
  build(quantity, variantTitle) {
    if (!this.enabled) return { ok: true };
    const picked = this.selected;
    if (!picked) {
      this.showError(this.dataset.error || '');
      this.types[0]?.focus();
      return { ok: false };
    }
    const isNew = picked.dataset.logoType === 'new';
    const forLabel = [this.dataset.for, variantTitle].filter(Boolean).join(' - ');
    const attributes = [{ key: 'Logo embroidery', value: picked.value }];
    const lines = [{
      merchandiseId: this.dataset.variantId,
      quantity,
      attributes: [{ key: 'Embroidery type', value: 'Logo' }, ...attributes, { key: 'For', value: forLabel }],
    }];
    if (isNew && this.dataset.feeVariantId) {
      lines.push({
        merchandiseId: this.dataset.feeVariantId,
        quantity: 1,
        attributes: [{ key: 'For', value: forLabel }],
      });
    } else if (isNew) {
      attributes.push({ key: 'Digitizing fee', value: 'To be invoiced' });
    }
    return { ok: true, attributes, lines };
  }
}

if (!customElements.get('luuna-logo-embroidery')) {
  customElements.define('luuna-logo-embroidery', LuunaLogoEmbroidery);
}
